import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useLocation, useParams } from "wouter";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslations } from "@/lib/translations";
import { usePageMeta } from "@/hooks/use-page-meta";
import { isWorkEmail } from "@workspace/email-policy";
import { ArrowLeft, ArrowRight, Send } from "lucide-react";
import CaptchaField from "@/components/CaptchaField";
import { useBookDemo } from "@/components/BookDemoProvider";
import companiesData from "@/data/companies.json";

interface CompanyEntry {
  Sector: string;
}

const allSectorsList = [
  ...new Set((companiesData as CompanyEntry[]).map((c) => c.Sector)),
].sort();

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  sector: string;
  message: string;
  website: string;
}

interface Slot {
  startUtc: string;
  endUtc: string;
}

interface DayGroup {
  key: string;
  label: string;
  slots: Slot[];
}

const EMPTY: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  sector: "",
  message: "",
  website: "",
};

const localTimeZone =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "UTC";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function BookDemo() {
  const { t, language, isRTL } = useTranslations();
  const [, navigate] = useLocation();
  const params = useParams<{ step?: string }>();
  const onSchedule = params.step === "schedule";

  const { contextItems, contextPayload, setBookingResult } = useBookDemo();

  usePageMeta({
    title: "Book a demo | ESG Invest",
    description:
      "Book a live demo of the ESG Invest Professional plan and pick a time that works for you.",
    path: "/book-demo",
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const update = (field: keyof FormState, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  const detailsValid =
    form.name.trim() !== "" &&
    isValidEmail(form.email) &&
    form.company.trim() !== "" &&
    form.phone.trim() !== "";

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    if (typeof window === "undefined") return;
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push({
      event: "virtual_pageview",
      page_path: "/book-demo",
      page_title: "Book a demo - ESG Invest",
      page_location:
        typeof window.location !== "undefined"
          ? window.location.origin + "/book-demo"
          : "/book-demo",
      locale: language,
    });
  }, [language]);

  const loadAvailability = async () => {
    setLoadingSlots(true);
    setError(null);
    try {
      const res = await fetch("/api/availability", { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { slots: Slot[] };
      setSlots(data.slots ?? []);
    } catch {
      setError(t("booking.availabilityError"));
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (!onSchedule) return;
    if (!detailsValid) {
      navigate("/book-demo", { replace: true });
      return;
    }
    if (slots.length === 0 && !loadingSlots) {
      void loadAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSchedule]);

  const dayGroups = useMemo<DayGroup[]>(() => {
    const dayFmt = new Intl.DateTimeFormat(language === "ar" ? "ar" : "en", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
    const groups = new Map<string, DayGroup>();
    for (const slot of slots) {
      const d = new Date(slot.startUtc);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      let group = groups.get(key);
      if (!group) {
        group = { key, label: dayFmt.format(d), slots: [] };
        groups.set(key, group);
      }
      group.slots.push(slot);
    }
    return Array.from(groups.values());
  }, [slots, language]);

  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(language === "ar" ? "ar" : "en", {
        hour: "numeric",
        minute: "2-digit",
      }),
    [language],
  );

  const handleContinue = async () => {
    setError(null);
    if (!isWorkEmail(form.email)) {
      setError(t("lead.form.workEmailRequired"));
      return;
    }
    setSelectedSlot("");
    navigate("/book-demo/schedule");
  };

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!selectedSlot) {
      setError(t("booking.selectSlotFirst"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          sector: form.sector || null,
          message: form.message.trim() || null,
          startUtc: selectedSlot,
          locale: language,
          context: contextPayload ?? null,
          website: form.website,
          captchaToken,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });
      if (!res.ok) {
        let reason: string | undefined;
        try {
          const body = (await res.json()) as { reason?: string };
          reason = body.reason;
        } catch {
          /* ignore */
        }
        if (reason && reason.startsWith("captcha")) {
          setCaptchaToken("");
          setCaptchaAnswer("");
          throw new Error(t("lead.form.captchaWrong"));
        }
        if (reason === "email_not_work") {
          throw new Error(t("lead.form.workEmailRequired"));
        }
        if (reason === "slot_taken") {
          setSelectedSlot("");
          await loadAvailability();
          throw new Error(t("booking.slotTaken"));
        }
        throw new Error(t("lead.toast.errorDesc"));
      }

      if (typeof window !== "undefined") {
        const w = window as unknown as {
          dataLayer?: Array<Record<string, unknown>>;
        };
        w.dataLayer = w.dataLayer ?? [];
        w.dataLayer.push({
          event: "form_lead_submitted",
          source: "pro_book_demo",
          locale: language,
        });
      }

      setBookingResult({ startUtc: selectedSlot, email: form.email.trim() });
      navigate("/bookeddemo");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t("lead.toast.errorDesc");
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const tzNote = t("booking.timezoneNote").replace("{tz}", localTimeZone);
  const captchaReady = captchaToken.trim() !== "" && captchaAnswer.trim() !== "";
  const sectors = allSectorsList;

  return (
    <Layout>
      <section className="py-16 md:py-24" dir={isRTL ? "rtl" : "ltr"}>
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold text-foreground mb-2"
              data-testid="text-book-demo-title"
            >
              {t("lead.pro.title")}
            </h1>
            <p className="text-muted-foreground">{t("lead.pro.desc")}</p>
          </div>

          <div
            className="flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground mb-8"
            data-testid="booking-steps"
          >
            <span className={!onSchedule ? "text-accent" : ""}>
              1. {t("booking.step.details")}
            </span>
            <span aria-hidden="true">→</span>
            <span className={onSchedule ? "text-accent" : ""}>
              2. {t("booking.step.time")}
            </span>
          </div>

          <Card className="p-6 md:p-8 border-0 shadow-lg">
            {!onSchedule && (
              <div className="space-y-4">
                {contextItems && contextItems.length > 0 && (
                  <div
                    className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-1"
                    data-testid="booking-context"
                  >
                    {contextItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between gap-3">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-medium text-foreground text-end">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="booking-name">
                    {t("lead.form.name")}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="booking-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    maxLength={200}
                    data-testid="input-booking-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking-email">
                    {t("lead.form.email")}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="booking-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    maxLength={320}
                    data-testid="input-booking-email"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="booking-company">
                      {t("lead.form.company")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="booking-company"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      maxLength={200}
                      data-testid="input-booking-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-phone">
                      {t("lead.form.phone")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="booking-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      maxLength={50}
                      data-testid="input-booking-phone"
                    />
                  </div>
                </div>

                {sectors.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="booking-sector">
                      {t("booking.form.sector")}
                    </Label>
                    <Select
                      value={form.sector}
                      onValueChange={(v) => update("sector", v)}
                    >
                      <SelectTrigger
                        id="booking-sector"
                        data-testid="select-booking-sector"
                      >
                        <SelectValue
                          placeholder={t("booking.form.sectorPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="booking-message">
                    {t("lead.form.message")}
                  </Label>
                  <Textarea
                    id="booking-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    maxLength={4000}
                    rows={3}
                    placeholder={t("lead.form.messagePlaceholder")}
                    data-testid="input-booking-message"
                  />
                </div>

                <div className="hidden" aria-hidden="true">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </label>
                </div>

                {error && (
                  <p
                    className="text-sm text-destructive"
                    role="alert"
                    data-testid="booking-error"
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="button"
                  variant="accent"
                  size="lg"
                  className="w-full"
                  disabled={!detailsValid}
                  onClick={handleContinue}
                  data-testid="button-booking-continue"
                >
                  {t("booking.continue")}
                  <ArrowRight
                    className={`w-4 h-4 ${isRTL ? "mr-2 rotate-180" : "ml-2"}`}
                  />
                </Button>
              </div>
            )}

            {onSchedule && (
              <form onSubmit={handleConfirm} className="space-y-4" noValidate>
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="booking-tz-note"
                >
                  {tzNote}
                </p>

                {loadingSlots ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {t("booking.loadingSlots")}
                  </p>
                ) : dayGroups.length === 0 ? (
                  <p
                    className="text-sm text-muted-foreground py-6 text-center"
                    data-testid="booking-no-slots"
                  >
                    {t("booking.noSlots")}
                  </p>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                    {dayGroups.map((group) => (
                      <div key={group.key} className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground sticky top-0 bg-background py-1">
                          {group.label}
                        </h4>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {group.slots.map((slot) => {
                            const selected = selectedSlot === slot.startUtc;
                            return (
                              <button
                                key={slot.startUtc}
                                type="button"
                                onClick={() => setSelectedSlot(slot.startUtc)}
                                className={`rounded-md border px-2 py-2 text-sm transition-colors ${
                                  selected
                                    ? "border-accent bg-accent text-accent-foreground"
                                    : "border-input hover:border-accent hover:bg-accent/10"
                                }`}
                                data-testid={`slot-${slot.startUtc}`}
                              >
                                {timeFmt.format(new Date(slot.startUtc))}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {dayGroups.length > 0 && (
                  <CaptchaField
                    token={captchaToken}
                    onTokenChange={setCaptchaToken}
                    answer={captchaAnswer}
                    onAnswerChange={setCaptchaAnswer}
                    disabled={submitting}
                  />
                )}

                {error && (
                  <p
                    className="text-sm text-destructive"
                    role="alert"
                    data-testid="booking-error"
                  >
                    {error}
                  </p>
                )}

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setError(null);
                      navigate("/book-demo");
                    }}
                    data-testid="button-booking-back"
                  >
                    <ArrowLeft
                      className={`w-4 h-4 ${isRTL ? "ml-2 rotate-180" : "mr-2"}`}
                    />
                    {t("booking.back")}
                  </Button>
                  <Button
                    type="submit"
                    variant="accent"
                    size="lg"
                    className="flex-1"
                    disabled={submitting || !selectedSlot || !captchaReady}
                    data-testid="button-booking-confirm"
                  >
                    <Send className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`} />
                    {submitting ? t("booking.confirming") : t("booking.confirm")}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </section>
    </Layout>
  );
}
